import { inject, Injectable } from '@angular/core';
import { AuthContextService } from '@core/auth/context/auth-context.service';
import { APP_CONFIG, AppConfig } from '@core/config/app-config';
import { SOCKET_IO } from '@core/socket/socket.token';
import { Socket } from 'socket.io-client';

@Injectable({
  providedIn: 'root',
})
export class SocketSessionService {
  private readonly appConfig: AppConfig = inject(APP_CONFIG);
  private readonly authContext = inject(AuthContextService);
  private readonly socketIo = inject(SOCKET_IO);
  private static readonly namespace = 'v2/reports';
  private static readonly sessionIdStorageKey = 'X-Session-ID';
  private static readonly transactionIdStorageKey = 'X-Transaction-ID';

  private socket: Socket | null = null;
  private connectPromise: Promise<Socket> | null = null;
  private connectReject: ((error: Error) => void) | null = null;
  private connectCleanup: (() => void) | null = null;

  async connectIfNeeded(): Promise<Socket> {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (!this.socket) {
      this.socket = this.createSocket();
    } else {
      this.socket.auth = this.buildAuthPayload();
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = new Promise<Socket>((resolve, reject) => {
      this.connectReject = reject;
      const activeSocket = this.socket;
      if (!activeSocket) {
        this.connectPromise = null;
        this.connectReject = null;
        reject(new Error('Socket is unavailable.'));
        return;
      }

      const cleanup = () => {
        activeSocket.off('connect', onConnect);
        activeSocket.off('connect_error', onConnectError);
        this.connectPromise = null;
        this.connectReject = null;
        this.connectCleanup = null;
      };

      const onConnect = () => {
        cleanup();
        resolve(activeSocket);
      };

      const onConnectError = (error: Error) => {
        cleanup();
        reject(error);
      };

      this.connectCleanup = cleanup;
      activeSocket.on('connect', onConnect);
      activeSocket.on('connect_error', onConnectError);
      activeSocket.connect();
    });

    return this.connectPromise;
  }

  isConnected(): boolean {
    return this.socket?.connected === true;
  }

  disconnect(): void {
    if (this.connectReject) {
      const rejectPendingConnect = this.connectReject;
      this.connectCleanup?.();
      rejectPendingConnect(new Error('Socket connection was cancelled.'));
      this.connectReject = null;
      this.connectCleanup = null;
      this.connectPromise = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectPromise = null;
  }

  private createSocket(): Socket {
    const authPayload = this.buildAuthPayload();

    const socket = this.socketIo(this.getSocketUrl(), {
      transports: ['websocket'],
      autoConnect: false,
      auth: authPayload,
    });
    socket.auth = authPayload;

    socket.on('disconnect', () => {
      if (this.socket !== socket) {
        return;
      }
      this.clearPendingConnectionState();
    });

    return socket;
  }

  private clearPendingConnectionState(): void {
    const rejectPendingConnect = this.connectReject;
    this.connectCleanup?.();
    this.connectReject = null;
    this.connectCleanup = null;
    this.connectPromise = null;
    rejectPendingConnect?.(new Error('Connection lost while connecting.'));
  }

  private getSocketUrl(): string {
    const socketBaseUrl = (this.appConfig.sockApiUrl || this.appConfig.apiUrl)?.replace(
      /\/+$/,
      '',
    );
    if (!socketBaseUrl) {
      throw new Error('Missing socket API URL configuration.');
    }

    return `${socketBaseUrl}/${SocketSessionService.namespace}`;
  }

  private buildAuthPayload(): {
    token: string;
    sessionId: string;
    transactionId: string;
  } {
    const token = this.authContext.getActiveOktaAuth().getAccessToken();
    if (!token) {
      throw new Error('Unable to open socket connection without an access token.');
    }

    return {
      token: `Bearer ${token}`,
      sessionId: this.getOrCreateStorageValue(SocketSessionService.sessionIdStorageKey),
      transactionId: this.getOrCreateStorageValue(SocketSessionService.transactionIdStorageKey),
    };
  }

  private getOrCreateStorageValue(key: string): string {
    try {
      const existingValue = localStorage.getItem(key);
      if (existingValue) {
        return existingValue;
      }
      const generatedValue = crypto.randomUUID();
      localStorage.setItem(key, generatedValue);
      return generatedValue;
    } catch {
      const rnd = (): string =>
        Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      return `${rnd()}${rnd()}-${rnd()}-${rnd()}-${rnd()}-${rnd()}${rnd()}${rnd()}`;
    }
  }
}
